import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { loadBook } from "../services/bookService";
import { Book } from "../types/Book";
import OptimizedImage from "./OptimizedImage";
import TrailerPlayer from "./TrailerPlayer";
import LoadingSpinner from "./LoadingSpinner";
import analytics from "../services/analytics";
import "./BookPage.css";

const BookPage: React.FC = () => {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTrailer, setShowTrailer] = useState(false);

  useEffect(() => {
    const fetchBook = async () => {
      if (!bookId) return;

      try {
        setLoading(true);
        const loadedBook = await loadBook(bookId);
        if (loadedBook) {
          setBook(loadedBook);
          
          // Track book page view
          analytics.trackBookPageViewed(
            bookId,
            loadedBook.title,
            document.referrer.includes(window.location.origin) ? 'homepage' : 'direct_link'
          );
          
          setError(null);
        } else {
          setError("Book not found");
        }
      } catch (err) {
        console.error("Error loading book:", err);
        setError("Failed to load book");
        analytics.trackError("Failed to load book", "BookPage");
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [bookId]);

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleListenToBook = () => {
    if (book) {
      // Track book listen start from book page
      analytics.trackBookListenStarted(
        book.id,
        book.title,
        'book_page'
      );
      
      navigate(`/book/${book.id}/reader`);
    }
  };

  const handlePlayTrailer = () => {
    if (book?.audio_trailer) {
      setShowTrailer(true);
      
      // Track trailer play
      analytics.trackTrailerPlayed(
        book.id,
        book.title,
        'book_page'
      );
    }
  };

  const handleTrailerEnded = () => {
    setShowTrailer(false);
  };

  if (loading) {
    return (
      <div className="book-page">
        <LoadingSpinner message="Loading book details..." size="large" />
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="book-page">
        <div className="error-message">
          <p>{error || "Book not found"}</p>
          <button onClick={handleBackToHome} className="back-button">
            ← Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="book-page">
      <header className="book-page-header fade-in">
        <button onClick={handleBackToHome} className="back-button">
          ← Back to Library
        </button>
      </header>

      <main className="book-page-content fade-in">
        <div className="book-details-container">
          <div className="book-cover-section">
            <div className="book-cover">
              <OptimizedImage
                src={book.cover_image}
                alt={book.title}
                lazy={false}
                priority={true}
                className="cover-image"
              />
            </div>
          </div>

          <div className="book-info-section">
            <div className="book-header">
              <h1 className="book-title">{book.title}</h1>
              <p className="book-author">by {book.author}</p>
            </div>

            <div className="book-metadata">
              <div className="metadata-grid">
                <div className="metadata-item">
                  <label>Genre:</label>
                  <span>{book.genre}</span>
                </div>
                <div className="metadata-item">
                  <label>Target Audience:</label>
                  <span>{book.target_age}</span>
                </div>
                <div className="metadata-item">
                  <label>Duration:</label>
                  <span>{book.estimated_duration}</span>
                </div>
                {book.author_website && (
                  <div className="metadata-item">
                    <label>Author Website:</label>
                    <a 
                      href={book.author_website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="author-link"
                    >
                      Visit Author's Site
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div className="book-logline">
              <h3>About This Story</h3>
              <p>{book.logline}</p>
            </div>

            <div className="book-actions">
              {book.audio_trailer && (
                <button 
                  onClick={handlePlayTrailer}
                  className="trailer-button secondary-button"
                  disabled={showTrailer}
                >
                  {showTrailer ? "Playing Trailer..." : "▶ Play Trailer"}
                </button>
              )}
              
              <button 
                onClick={handleListenToBook}
                className="listen-button primary-button"
              >
                🎧 Listen to Book
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Trailer Player */}
      {showTrailer && book.audio_trailer && (
        <div className="trailer-player-container">
          <TrailerPlayer
            bookId={book.id}
            audioFile={book.audio_trailer}
            bookTitle={book.title}
            chapterTitle="Trailer"
            onAudioEnded={handleTrailerEnded}
            onAudioStateChange={() => {}}
          />
        </div>
      )}
    </div>
  );
};

export default BookPage;